import {
  Component,
  OnInit,
  computed,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { ImageFallbackService } from '../services/image-fallback.service';
import { NotificationService } from '../services/notification.service';
import { ActivatedRoute } from '@angular/router';
import { FollowersService } from '../services/followers.service';
import { ProjectService } from '../services/project.service';
import { Subscription, takeUntil, delay, of } from 'rxjs';
import { Router } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import {
  getProjectTypeLabel,
  getProjectTypeColorClass,
} from '../enums/project-type.enum';
import { EmojiParserPipe } from '../pipes/emoji-parser.pipe';
import { NgOptimizedImage } from '@angular/common';
import { ProjectUserOwned } from '../models';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ReportModalComponent } from '../report-modal/report-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type ProjectSort = 'newest' | 'liked' | 'oldest';

@Component({
  selector: 'app-cuenta',
  templateUrl: './account.component.html',
  standalone: true,
  imports: [
    CommonModule,
    EmojiParserPipe,
    NgOptimizedImage,
    MatDialogModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class AccountComponent implements OnInit, OnDestroy {
  user: any = {};
  currentUserId = computed(() => this.userService.userProfile()?.id || null);
  isFollowing: boolean = false;

  isLoadingProfile: boolean = true;
  isLoadingStats: boolean = true;
  isLoadingProjects: boolean = true;

  followOperationInProgress = false;

  private userProfileSub!: Subscription;
  private routeSub!: Subscription;
  private destroy$ = new Subject<void>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  showLeftArrow = false;
  showRightArrow = true;
  projects: ProjectUserOwned[] = [];

  sortOption: ProjectSort = 'newest';
  private allProjects: ProjectUserOwned[] = [];

  get isLoggedIn(): boolean {
    return this.authService.checkAuth();
  }

  get isEmailVerified(): boolean {
    return this.authService.emailVerified();
  }

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private followersService: FollowersService,
    private projectService: ProjectService,
    private router: Router,
    private imageFallback: ImageFallbackService,
    private dialog: MatDialog,
    private notifications: NotificationService,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) {}

  get isPro(): boolean {
    return this.userService.isProUser();
  }

  get joinedDate(): string {
    return this.user.created_at
      ? this.datePipe.transform(this.user.created_at, 'longDate') || ''
      : '';
  }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(() => {
      this.loadUserProfile();
    });
  }

  loadUserProfile(): void {
    this.isLoadingProfile = true;
    this.isLoadingStats = true;
    this.isLoadingProjects = true;

    const username = this.cleanUsername(
      this.route.snapshot.paramMap.get('username') || '',
    );

    this.userService.getUserByUsername(username).subscribe({
      next: (response) => {
        if (response.status) {
          this.user = response.user;
          this.isLoadingProfile = false;

          if (this.currentUserId() && this.user.id !== this.currentUserId()) {
            this.isFollowing = !!this.user.is_following;
          } else {
            this.isFollowing = false;
          }

          of(true)
            .pipe(delay(100))
            .subscribe(() => {
              this.isLoadingStats = false;
            });

          of(true)
            .pipe(delay(200))
            .subscribe(() => {
              this.loadUserProjects(this.user.id);
            });
        }
      },
      error: () => {
        this.isLoadingProfile = false;
        this.isLoadingStats = false;
        this.isLoadingProjects = false;
      },
    });
  }

  scroll(direction: -1 | 1): void {
    const container = this.scrollContainer.nativeElement;
    container.scrollBy({
      left: container.clientWidth * 0.8 * direction,
      behavior: 'smooth',
    });
  }

  updateScrollButtons(): void {
    if (!this.scrollContainer) return;
    const el = this.scrollContainer.nativeElement;
    const tolerance = 0;
    const canScroll = el.scrollWidth > el.clientWidth + tolerance;
    this.showLeftArrow = canScroll && el.scrollLeft > tolerance;
    this.showRightArrow =
      canScroll && el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance;
  }

  loadUserProjects(userId: number): void {
    this.isLoadingProjects = true;
    this.projectService.getPublicProjectsByUser(userId).subscribe({
      next: (response) => {
        if (response.status) {
          this.allProjects = response.projects;
          this.applySort();
          setTimeout(() => this.updateScrollButtons(), 0);
        }
        this.isLoadingProjects = false;
      },
      error: () => {
        this.isLoadingProjects = false;
      },
    });
  }

  setSort(option: ProjectSort): void {
    if (this.sortOption === option) return;
    this.sortOption = option;
    this.applySort();
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
    setTimeout(() => this.updateScrollButtons(), 350);
  }

  private applySort(): void {
    const sorted = [...this.allProjects];
    const date = (p: ProjectUserOwned) =>
      p.created_at ? new Date(p.created_at).getTime() : 0;

    switch (this.sortOption) {
      case 'liked':
        sorted.sort((a, b) => (b.likes_count ?? 0) - (a.likes_count ?? 0));
        break;
      case 'oldest':
        sorted.sort((a, b) => date(a) - date(b));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => date(b) - date(a));
        break;
    }

    this.projects = sorted;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateScrollButtons();
  }

  toggleFollow(): void {
    if (this.followOperationInProgress) return;

    if (!this.isLoggedIn) {
      this.notifications.show(
        'login',
        this.translate.instant('ACCOUNT.NOTIF_LOGIN_TITLE'),
        this.translate.instant('ACCOUNT.NOTIF_FOLLOW_LOGIN'),
      );
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    if (!this.isEmailVerified) {
      this.notifications.show(
        'verify',
        this.translate.instant('ACCOUNT.NOTIF_VERIFY_TITLE'),
        this.translate.instant('ACCOUNT.NOTIF_FOLLOW_VERIFY'),
      );
      this.router.navigate(['/verify-email']);
      return;
    }

    const wasFollowing = this.isFollowing;
    const originalCount = this.user.followers_count || 0;
    this.followOperationInProgress = true;
    this.isFollowing = !wasFollowing;
    this.user.followers_count = wasFollowing
      ? Math.max(0, originalCount - 1)
      : originalCount + 1;

    const operation = wasFollowing
      ? this.followersService.unfollowUser(this.user.id)
      : this.followersService.followUser(this.user.id);

    operation.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.followOperationInProgress = false;
      },
      error: () => {
        this.isFollowing = wasFollowing;
        this.user.followers_count = originalCount;
        this.followOperationInProgress = false;
      },
    });
  }

  goToForm(project: any): void {
    if (project?.idform && project?.slug) {
      this.router.navigate(['/games/view-form', project.idform, project.slug]);
    }
  }

  openReportModal(): void {
    if (!this.isLoggedIn) {
      this.notifications.show(
        'login',
        this.translate.instant('ACCOUNT.NOTIF_LOGIN_TITLE'),
        this.translate.instant('ACCOUNT.NOTIF_REPORT_LOGIN'),
      );
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    if (!this.isEmailVerified) {
      this.notifications.show(
        'verify',
        this.translate.instant('ACCOUNT.NOTIF_VERIFY_TITLE'),
        this.translate.instant('ACCOUNT.NOTIF_REPORT_VERIFY'),
      );
      this.router.navigate(['/verify-email']);
      return;
    }

    if (!this.user) return;

    this.dialog.open(ReportModalComponent, {
      data: {
        reportableType: 'user',
        reportableId: this.user.id,
        displayName: this.user.name || this.user.username,
      },
      maxWidth: '95vw',
    });
  }

  handleImageError(event: Event, type: 'banner' | 'profile' | 'project'): void {
    this.imageFallback.handleImageError(event, type);
  }

  get safeBannerUrl(): string {
    const profile = this.user?.profile;
    // Prioridad: Imagen optimizada > Imagen normal > Fallback
    const path = profile?.optimizedBanner || profile?.banner_url;
    return this.imageFallback.getConstructedUrl(path, 'banner');
  }

  get safeProfilePictureUrl(): string {
    const profile = this.user?.profile;
    // Prioridad: Imagen optimizada > Imagen normal > Fallback
    const path = profile?.optimizedProfile || profile?.profile_picture_url;
    return this.imageFallback.getConstructedUrl(path, 'profile');
  }

  getSafeThumbnailUrl(project: any): string {
    const path = project?.optimizedThumbnail || project?.miniatura_url;
    return this.imageFallback.getConstructedUrl(path, 'project');
  }

  getProjectTypeLabel(t: number): string {
    return getProjectTypeLabel(t);
  }
  getProjectTypeColorClass(t: number): string {
    return getProjectTypeColorClass(t);
  }

  private cleanUsername(username: string): string {
    return username.startsWith('@') ? username.slice(1) : username;
  }

  ngOnDestroy(): void {
    if (this.userProfileSub) this.userProfileSub.unsubscribe();
    if (this.routeSub) this.routeSub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
